import { Fragment } from 'react';
import { IC, Svg } from '../art/icons.js';
import { LEGAL_DOCS, legalDoc, splitPlaceholders } from '../legal/content.js';
import { useNav } from '../lib/router.js';
import { Scroll, TopBar } from '../components/ui.js';

/* The legal pages inside the app (the same text as the public /legal pages), so nobody has to leave the app to read them. */

function Text({ text }: { text: string }) {
  return <>{splitPlaceholders(text).map((part, i) => (i % 2 ? <mark key={i} className="legal-ph" title="Placeholder">{part}</mark> : <Fragment key={i}>{part}</Fragment>))}</>;
}

function Body({ lines }: { lines: string[] }) {
  const out: React.ReactNode[] = [];
  let list: string[] = [];
  const flush = (k: number) => {
    if (list.length) out.push(<ul key={`u${k}`} className="legal-list">{list.map((l, i) => <li key={i}><Text text={l} /></li>)}</ul>);
    list = [];
  };
  lines.forEach((l, i) => {
    if (l.startsWith('- ')) list.push(l.slice(2));
    else {
      flush(i);
      out.push(<p key={i} className="legal-p"><Text text={l} /></p>);
    }
  });
  flush(lines.length);
  return <>{out}</>;
}

export function LegalIndex() {
  const { navigate } = useNav();
  return (
    <Scroll padStyle={{ gap: 14, paddingTop: 16 }}>
      <TopBar title="Legal" />
      <div className="card rows">
        {LEGAL_DOCS.map((d) => (
          <button type="button" key={d.slug} onClick={() => navigate(`/legal/${d.slug}`)}><span className="grow col" style={{ gap: 2 }}><b>{d.title}</b><span className="s">{d.summary}</span></span><Svg html={IC.chev} /></button>
        ))}
      </div>
    </Scroll>
  );
}

export function LegalPage() {
  const { route } = useNav();
  const doc = legalDoc(route.params.id ?? '');
  if (!doc) return <LegalIndex />;
  const draft = JSON.stringify(doc).includes('[[');
  return (
    <Scroll padStyle={{ gap: 10, paddingTop: 16 }}>
      <TopBar title={doc.title} />
      <p className="small muted" style={{ margin: 0 }}>Last updated {doc.updated}</p>
      {draft && <p className="small muted legal-draft">Draft. Highlighted parts are still to be confirmed.</p>}
      {doc.sections.map((s) => (
        <section key={s.heading} className="col" style={{ gap: 4 }}>
          <h2 className="h2" style={{ marginTop: 12 }}>{s.heading}</h2>
          <Body lines={s.body} />
        </section>
      ))}
    </Scroll>
  );
}

/** "By joining you agree…" style line with links into the app's legal pages. */
export function LegalLinks({ lead, docs }: { lead: string; docs: ('privacy' | 'terms' | 'credit' | 'rewards' | 'catering')[] }) {
  const { navigate } = useNav();
  return (
    <p className="small muted legal-agree" style={{ margin: 0, textAlign: 'center' }}>
      {lead}{' '}
      {docs.map((slug, i) => (
        <Fragment key={slug}>
          {i > 0 && i === docs.length - 1 ? ' and ' : i > 0 ? ' ' : null}
          {/* The comma stays on the same line as the link it follows. */}
          <span style={{ whiteSpace: 'nowrap' }}><button type="button" className="link inline" onClick={() => navigate(`/legal/${slug}`)}>{slug === 'rewards' ? legalDoc(slug)!.title : legalDoc(slug)!.title.replace(/^./, (c) => c.toLowerCase())}</button>{i < docs.length - 2 ? ',' : i === docs.length - 1 ? '.' : ''}</span>
        </Fragment>
      ))}
    </p>
  );
}
